import { INSURANCE_COPY } from "./insurance.constants.ts";
import type { InsurancePanel as Panel } from "./insurance.model.ts";
import "./insurance.css";

/**
 * Who insures the work, on its record — OND-48.
 *
 * NO CONTROLLER AND NO STATE. `toInsurancePanel` decides everything from rows
 * `useWorkController` has already loaded, and the panel takes the answer as a
 * prop — `WorkAgentsPanel`'s shape rather than `LocationPanel`'s, because this
 * one never writes. The place to record a period is the Insurance chip under
 * "+ Add history", and there being one door is the point of the ticket: a second
 * editable insurer field here could disagree with the history, and a record that
 * says two things about the same policy says neither.
 *
 * THE DISPUTE IS SAID TWICE, and that is deliberate. Once at the top of the
 * panel, where a reader scanning for "is this insured" meets it before they take
 * any figure as settled; and once on the row it belongs to, so it is obvious
 * WHICH period is disputed when two of them overlap. A badge on the row alone
 * would let a reader take in "AXA · £1,850,000" and leave.
 */
export function InsurancePanel({
  panel,
  onOpenEntry,
}: {
  panel: Panel;
  /**
   * Focus the entry this period was read off.
   *
   * A handler and not a `<Link>`, because the panel is already ON the work's
   * record: the row sets `?entry=` on the page the reader is standing on, which
   * is what every other affordance here does. A constructed href would be a
   * second place that knows the record's URL shape.
   */
  onOpenEntry: (entryId: string) => void;
}) {
  if (panel.kind === "hidden") return null;

  return (
    <section className="insurance">
      <header className="insurance__head">
        <h2 className="insurance__title">{INSURANCE_COPY.title}</h2>
      </header>

      {panel.alert && (
        <p className="insurance__alert" role="status">
          <span className="insurance__alert-lead">⚠ {panel.alert.lead}</span>{" "}
          {panel.alert.detail}
        </p>
      )}

      <ul className="insurance__list">
        {panel.periods.map((period) => (
          <li
            key={period.entryId}
            className={`insurance__period insurance__period--${period.strip.tone}`}
          >
            <p className="insurance__insurer">{period.insurer}</p>
            <p className="insurance__dates">{period.period}</p>
            <p className="insurance__policy">{period.policy ?? INSURANCE_COPY.policyUnknown}</p>
            <p className="insurance__amount">{period.amount ?? INSURANCE_COPY.amountUnknown}</p>
            {/* The same stamp and the same words the timeline card wears, from
                the same two counts — so '2 confirmations · 1 dispute' reads the
                same on both surfaces and neither can conclude more than the
                other. */}
            <p className="insurance__state">
              <span className="insurance__stamp">{period.strip.pill}</span>
              <span className="insurance__summary">{period.strip.summary}</span>
            </p>
            {/* ?entry= outranks ?tab= on the record, so this lands on the row
                in the tab that holds every kind — where the reason for a
                dispute and the recorded responses are. */}
            <button
              type="button"
              className="insurance__link"
              onClick={() => onOpenEntry(period.entryId)}
            >
              {INSURANCE_COPY.openEntry}
            </button>
          </li>
        ))}
      </ul>

      {panel.note && <p className="insurance__note">{panel.note}</p>}
    </section>
  );
}
